import React from 'react';
import { sortWith, descend, pathEq, complement, compose, path, is, defaultTo, map, take } from 'ramda';
import { Row, Col } from 'react-flexbox-grid';

import HistoryEntry from 'components/HistoryEntry';
import HistoryEntryHeader from 'components/HistoryEntryHeader';
import HistoryEntryColumns from 'components/HistoryEntryColumns';
import HistoryEntryColumn from 'components/HistoryEntryColumn';
import CommonHistoryEntryControls from 'routes/Documents/components/CommonHistoryEntryControls';
import CommonViewButton from 'routes/Documents/components/CommonViewButton';

import { validate, formatDate, dateValue } from 'lib/dateDisplay';
import { formatStrength } from '../../formatters';
import { getLatestFinishedCommitData } from 'lib/documentHandling';

const isInUse = complement(pathEq(['content', 'hasEnded'], 'yes'));

export const sorter = sortWith([descend(isInUse), descend(compose(dateValue, path(['content', 'start'])))]);

const msg = id => ({ id });
const accessOpt = opt => (opt ? msg(`medication.opts.${opt}`) : msg('errors.unhandled'));
const safeArrayPath = (p, obj) => compose(defaultTo([]), path(p || []))(obj);

const formatDrugHeader = drug => (is(Object, drug) ? `${drug.name} ${drug.dosageForm} ${formatStrength(drug)}` : null);

const formatVitaminHeader = content => {
  let vitamin = '';
  let company = '';
  if (content.vitamin) vitamin = content.vitamin;
  if (content.vitaminCompany) company = ' (' + content.vitaminCompany + ')';

  return `${vitamin}${company}`;
};

const formatUsageString = (d, fm) =>
  path(['content', 'amount'], d) && path(['content', 'interval'], d) && path(['content', 'intervalType'], d)
    ? `${fm(
        { id: 'medication.usageString' },
        {
          amount: path(['content', 'amount'], d),
          times: path(['content', 'interval'], d),
          when: path(['content', 'intervalType'], d),
        },
      )}`
    : null;

const formatMedDate = (drug, fm) =>
  !isInUse(drug) && validate(path(['content', 'end'], drug))
    ? `${formatDate(path(['content', 'start'], drug))} - ${formatDate(path(['content', 'end'], drug))}`
    : validate(path(['content', 'start'], drug)) &&
      fm(msg('medication.dates.inUseFrom'), { startDate: formatDate(path(['content', 'start'], drug)) });

const formatEvents = fm => ev => (
  <Row style={{ marginBottom: '2rem' }} key={ev.timestamp}>
    <Col xs={12}>
      <p>
        {ev.type && fm(accessOpt(ev.type))} ({validate(ev.date) && formatDate(ev.date)})
      </p>
    </Col>
    <Col xs={12}>{ev.detail && ev.detail.length > 0 && <span>{ev.detail}</span>}</Col>
  </Row>
);

const sortEvents = descend(compose(dateValue, path(['date'])));

const mapper =
  (name, edit, view) =>
  (d, { formatMessage }) => (
    <HistoryEntry key={d.documentId}>
      <HistoryEntryHeader
        title={d.documentType === 'medication' ? formatDrugHeader(d.content.drug) : formatVitaminHeader(d.content)}
        active={isInUse(d)}
      >
        {window.__ROLE__ !== 'DOCTOR' && (
          <CommonHistoryEntryControls action={() => edit(d.documentType, d.documentId, getLatestFinishedCommitData(d))}>
            {isInUse(d) && (
              <h6 style={{ textTransform: 'uppercase' }}>{formatMessage(msg('medication.labels.inUse'))}</h6>
            )}
          </CommonHistoryEntryControls>
        )}
        {window.__ROLE__ === 'DOCTOR' && <CommonViewButton onView={e => view(name, d.documentId)} />}
      </HistoryEntryHeader>

      <HistoryEntryColumns>
        {d.documentType === 'medication' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.labels.inUse'))} smallScaling={12}>
            <p>{formatMedDate(d, formatMessage)}</p>
          </HistoryEntryColumn>
        )}

        {d.documentType === 'vitamin' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.labels.inUse'))} smallScaling={12}>
            <p>{formatMedDate(d, formatMessage)}</p>
          </HistoryEntryColumn>
        )}

        {d.documentType === 'medication' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.labels.substances'))} smallScaling={12}>
            <p>
              {Array.isArray(path(['content', 'drug', 'substances'], d))
                ? safeArrayPath(['content', 'drug', 'substances'], d).join(', ')
                : path(['content', 'drug', 'substances'], d)}
            </p>
          </HistoryEntryColumn>
        )}

        {d.documentType === 'medication' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.sections.usageType'))} smallScaling={12}>
            {path(['content', 'usageType'], d) && (
              <div>
                <p>{formatMessage(accessOpt(path(['content', 'usageType'], d)))}</p>
                {path(['content', 'usageType'], d) === 'regular' && <p>{formatUsageString(d, formatMessage)}</p>}
              </div>
            )}
          </HistoryEntryColumn>
        )}

        {d.documentType === 'vitamin' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.labels.dosage'))} smallScaling={12}>
            <p>{d.content.vitaminDosage}</p>
          </HistoryEntryColumn>
        )}

        {d.documentType === 'vitamin' && (
          <HistoryEntryColumn header={formatMessage(msg('medication.sections.vitamin.usageType'))} smallScaling={12}>
            {path(['content', 'usageType'], d) && (
              <div>
                <p>{formatMessage(accessOpt(path(['content', 'usageType'], d)))}</p>
                {path(['content', 'usageType'], d) === 'regular' && <p>{formatUsageString(d, formatMessage)}</p>}
              </div>
            )}
          </HistoryEntryColumn>
        )}

        <HistoryEntryColumn header={formatMessage(msg('medication.sections.eventsDuringUse'))} smallScaling={12}>
          {map(formatEvents(formatMessage), take(3, safeArrayPath(['content', 'events'], d).sort(sortEvents)))}
          {safeArrayPath(['content', 'events'], d).length > 3 && (
            <Row>
              <Col xs>
                <h6>
                  {formatMessage(msg('medication.labels.moreEvents'), {
                    more: safeArrayPath(['content', 'events'], d).length - 3,
                  })}
                </h6>
              </Col>
            </Row>
          )}
        </HistoryEntryColumn>
      </HistoryEntryColumns>
    </HistoryEntry>
  );

export default mapper;
